import { forwardRef, useCallback, useImperativeHandle, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowUpOutline, Flex, HStack, IconButton, MicrophoneOutline, StopAltSolid, StopSolid, Textarea, Tooltip } from '@bonusly/design-system';

const DEFAULT_MAX_HEIGHT = 120;

export interface BizyComposerHandle {
	/** Imperatively sets the textarea value (voice transcript, cancel restore, contextual inserts). */
	setInputText: (text: string) => void;
	focusInput: () => void;
}

export interface BizyComposerProps {
	onSubmit: (content: string) => void;
	onCancel?: () => void;
	isLoading?: boolean;
	disabled?: boolean;
	placeholder?: string;
	fontSize?: string | object;
	/** Max textarea height in px. Defaults to 120 (side panel). Desktop uses 200. */
	maxHeight?: number;
	/** Left footer slot — prompt library menu, model selector, etc. Rendered inside an HStack. */
	footerLeft?: React.ReactNode;
	/** Voice mic toggle. The mic button is only rendered when this is provided. */
	onVoiceToggle?: () => void;
	isVoiceRecording?: boolean;
	isVoiceStarting?: boolean;
	/** Called on every input change with the current raw value. Use for analytics / unread acknowledgement. */
	onInputChange?: (value: string) => void;
	/**
	 * Called when the input transitions between empty and non-empty, whatever
	 * the source — typing, imperative setInputText (voice transcript, cancel
	 * restore, prefills), or the submit clear. The single source of truth for
	 * "the user has a draft" (BIZY-683 idle deferral), so no path can forget.
	 */
	onDraftStateChange?: (hasDraft: boolean) => void;
	onFocus?: () => void;
	onBlur?: () => void;
	onClick?: () => void;
	/** Maps to data-pane-target on the underlying textarea (used by desktop Cmd+2 shortcut). */
	paneTarget?: string;
}

export const BizyComposer = forwardRef<BizyComposerHandle, BizyComposerProps>(function BizyComposer(
	{
		onSubmit,
		onCancel,
		isLoading = false,
		disabled = false,
		placeholder,
		fontSize,
		maxHeight = DEFAULT_MAX_HEIGHT,
		footerLeft,
		onVoiceToggle,
		isVoiceRecording = false,
		isVoiceStarting = false,
		onInputChange,
		onDraftStateChange,
		onFocus,
		onBlur,
		onClick,
		paneTarget,
	},
	ref
) {
	const { t } = useTranslation('admin_bizy');
	const inputRef = useRef<HTMLTextAreaElement>(null);

	// Tracks only whether the input is empty — not the full value — so typing
	// does not cause the parent to re-render on every keystroke.
	const [inputIsEmpty, setInputIsEmpty] = useState(true);
	const inputIsEmptyRef = useRef(true);

	// Ref'd so the stable callbacks below always notify the latest listener.
	const onDraftStateChangeRef = useRef(onDraftStateChange);
	onDraftStateChangeRef.current = onDraftStateChange;

	// Single choke point for the empty ⇆ non-empty transition, whatever the
	// text source (typing, imperative set, submit clear).
	const updateInputIsEmpty = useCallback((isEmpty: boolean) => {
		if (isEmpty === inputIsEmptyRef.current) return;
		inputIsEmptyRef.current = isEmpty;
		setInputIsEmpty(isEmpty);
		onDraftStateChangeRef.current?.(!isEmpty);
	}, []);

	const inputDisabled = isLoading || disabled;

	const setInputText = useCallback(
		(text: string) => {
			if (inputRef.current) {
				inputRef.current.value = text;
				inputRef.current.style.height = 'auto';
				inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, maxHeight)}px`;
			}
			updateInputIsEmpty(!text.trim());
		},
		[maxHeight, updateInputIsEmpty]
	);

	useImperativeHandle(
		ref,
		() => ({
			setInputText,
			focusInput: () => inputRef.current?.focus(),
		}),
		[setInputText]
	);

	const handleSend = useCallback(() => {
		const value = inputRef.current?.value ?? '';
		if (!value.trim() || inputDisabled) return;
		onSubmit(value.trim());
		if (inputRef.current) {
			inputRef.current.value = '';
			inputRef.current.style.height = 'auto';
			inputRef.current.focus();
		}
		updateInputIsEmpty(true);
	}, [inputDisabled, onSubmit, updateInputIsEmpty]);

	const showVoiceButton = Boolean(onVoiceToggle) && !isLoading && (inputIsEmpty || isVoiceRecording || isVoiceStarting);

	return (
		<Flex direction="column" gap={1} border="1px solid" borderColor="border.default" borderRadius="lg" p={3} bg="bg.surface" position="relative">
			<Textarea
				ref={inputRef}
				onChange={(e) => {
					updateInputIsEmpty(!e.target.value.trim());
					e.target.style.height = 'auto';
					e.target.style.height = `${Math.min(e.target.scrollHeight, maxHeight)}px`;
					onInputChange?.(e.target.value);
				}}
				onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
					if (e.key === 'Enter' && !e.shiftKey) {
						e.preventDefault();
						handleSend();
					}
					if (e.key === 'Escape' && isLoading) {
						e.preventDefault();
						onCancel?.();
					}
				}}
				onFocus={onFocus}
				onBlur={onBlur}
				onClick={onClick}
				placeholder={placeholder ?? t('inputPlaceholder')}
				aria-label={t('ariaLabel.chatMessageInput')}
				variant="unstyled"
				fontSize={fontSize}
				rows={1}
				resize="none"
				minH="28px"
				maxH={`${maxHeight}px`}
				lineHeight="20px"
				borderRadius={0}
				px={0}
				py="2px"
				{...(paneTarget ? { 'data-pane-target': paneTarget } : {})}
			/>
			<Flex align="center" justify="space-between">
				<HStack spacing={1.5}>{footerLeft ?? null}</HStack>
				<HStack spacing={0}>
					{showVoiceButton && (
						<Tooltip label={t(isVoiceRecording ? 'voiceInput.stop' : 'voiceInput.start')} hasArrow={false}>
							<IconButton
								aria-label={t(isVoiceRecording ? 'voiceInput.stop' : 'voiceInput.start')}
								icon={isVoiceRecording ? <StopSolid boxSize={4} /> : <MicrophoneOutline boxSize={4} />}
								borderRadius="full"
								size="sm"
								minW={8}
								h={8}
								bg={isVoiceRecording ? 'error.bg' : 'transparent'}
								color={isVoiceRecording ? 'error.fg' : 'text.softest'}
								onClick={onVoiceToggle}
								isLoading={isVoiceStarting}
								isDisabled={inputDisabled}
								_hover={{ bg: isVoiceRecording ? 'error.bgHover' : 'secondary.bgAlpha' }}
								_disabled={{ opacity: 0.2, cursor: 'not-allowed' }}
							/>
						</Tooltip>
					)}
					<IconButton
						aria-label={isLoading ? t('ariaLabel.stopGeneration') : t('ariaLabel.sendMessage')}
						icon={isLoading ? <StopAltSolid boxSize={4} /> : <ArrowUpOutline boxSize={4} />}
						borderRadius="full"
						size="sm"
						minW={8}
						h={8}
						bg="secondary.bg"
						color="secondary.fgOnSurface"
						onClick={isLoading ? onCancel : handleSend}
						isDisabled={isLoading ? false : inputIsEmpty || inputDisabled}
						_hover={{ bg: 'secondary.bgHover' }}
						_disabled={{ opacity: 0.2, cursor: 'not-allowed' }}
					/>
				</HStack>
			</Flex>
		</Flex>
	);
});
